import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_NON_ATTENDANCE_APPLICATIONS } from '@/api/endpoints';
import { NON_ATTENDANCE_APPLICATION_QUERY_KEY } from '@/api/queryKeys';
import {
  CateringStatusType,
  type NonAttendanceApplicationDetail,
} from '@/components/AbsenceApplication/types';
import { unwrapAxiosResponse } from '@/utils/axios';
import { joinPath } from '@/utils/global';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;
const POLL_MAX_ATTEMPTS = POLL_TIMEOUT_MS / POLL_INTERVAL_MS;

interface Options {
  id: number | null;
  initialData?: NonAttendanceApplicationDetail;
  onTimeout?: () => void;
}

const isCateringPending = (
  application: NonAttendanceApplicationDetail | undefined,
): boolean =>
  application?.cateringRequest?.statusType === CateringStatusType.New;

export const useNonAttendanceApplication = ({
  id,
  initialData,
  onTimeout,
}: Options) => {
  const query = useQuery<NonAttendanceApplicationDetail>({
    queryKey: [...NON_ATTENDANCE_APPLICATION_QUERY_KEY, id],
    queryFn: () =>
      apiClient
        .get<NonAttendanceApplicationDetail>(
          joinPath(API_NON_ATTENDANCE_APPLICATIONS, String(id)),
        )
        .then(unwrapAxiosResponse),
    enabled: id !== null,
    initialData,
    refetchInterval: (currentQuery) => {
      const data = currentQuery.state.data;

      if (!isCateringPending(data)) {
        return false;
      }

      const attempts = currentQuery.state.dataUpdateCount;

      if (attempts >= POLL_MAX_ATTEMPTS) {
        onTimeout?.();
        return false;
      }

      return POLL_INTERVAL_MS;
    },
  });

  return {
    ...query,
    isPolling: isCateringPending(query.data),
  };
};
